import { StatusCodes } from 'http-status-codes';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../modules/profile/logic/auth';
import RouteKey from '../../routes/key';

type IsAuthorized = boolean;

interface ApiErrorHandlersReturn {
  /**
   * Function to handle session related api error.
   */
  sessionErrorHandler: (err: AxiosError | null, toRedirect?: boolean) => IsAuthorized;

  /**
   * Function to handle general api error
   */
  apiErrorHandler: (err: AxiosError | null, toRedirect?: boolean) => string;
}

const { error } = RouteKey.child;
const { auth: authRoute } = RouteKey.child;
const { login } = RouteKey.auth.child;
const { serverError, generic, serviceUnavailable } = RouteKey.error.child;
const {
  FORBIDDEN,
  IM_A_TEAPOT,
  NOT_FOUND,
  INTERNAL_SERVER_ERROR,
  BAD_GATEWAY,
  GATEWAY_TIMEOUT,
  SERVICE_UNAVAILABLE,
} = StatusCodes;

/**
 * This hook returns the centralised handler for Response Error, spilt into Session and API handlers
 *
 */
export function useApiErrorHandlers(): ApiErrorHandlersReturn {
  const history = useHistory();
  const auth = useAuth();
  const pendingNavigateRef = useRef<string | null>(null);

  // Defer navigation to avoid "Cannot update during an existing state transition" warning.
  // This happens when history.replace() is called during render (inside query onError/apiErrorHandler).
  useEffect(() => {
    if (pendingNavigateRef.current) {
      const target = pendingNavigateRef.current;
      pendingNavigateRef.current = null;
      history.replace(target);
    }
  }, [history]);

  const sessionErrorHandler = (err: AxiosError | null, toRedirect = true): IsAuthorized => {
    // Without error, considered as authorized
    if (!err) {
      return true;
    }

    // Without response (e.g. network error), treat as unauthorized
    if (!err.response) {
      return false;
    }

    switch (err.response.status) {
      case FORBIDDEN:
        if (toRedirect) {
          history.push(`/${error}/${generic}`);
        }

        return false;
      case IM_A_TEAPOT:
        auth.signout();
        auth.setUserSessionInactive();
        if (!history.location.pathname.includes(`/${authRoute}/${login}`)) {
          history.replace(`/${authRoute}/${login}`);
        }
        return false;
      default:
        // error status is not related to authorisation
        return true;
    }
  };

  const apiErrorHandler = (err: AxiosError | null, toRedirect = true): string => {
    if (!err?.response) {
      return '';
    }

    switch (err.response.status) {
      case NOT_FOUND:
        if (toRedirect && !history.location.pathname.includes(`/${error}`)) {
          pendingNavigateRef.current = `/${error}`;
        }

        return 'Api Route Not Found';
      case INTERNAL_SERVER_ERROR:
      case BAD_GATEWAY:
      case GATEWAY_TIMEOUT:
        if (toRedirect && !history.location.pathname.includes(`/${error}`)) {
          pendingNavigateRef.current = `/${error}/${serverError}`;
        }

        return 'Server Error';
      case SERVICE_UNAVAILABLE:
        if (toRedirect && !history.location.pathname.includes(`/${error}`)) {
          pendingNavigateRef.current = `/${error}/${serviceUnavailable}`;
        }

        return 'Service Unavailable';
      default:
        return '';
    }
  };

  return {
    sessionErrorHandler,
    apiErrorHandler,
  };
}
